import { useEffect, useState } from "react";
import {
  Search,
  Receipt,
  Edit,
  Trash2,
  X,
  Plus,
  LoaderCircle,
  User,
  Phone,
  Calendar,
  CheckCircle2,
  RotateCcw,
  Save,
  PackagePlus,
  History,
  Printer,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../constant/api";

// =====================================================
// 1. ADD / DECREASE QTY MODAL
// =====================================================
function AddQtyModal({ item, orderId, onClose, onSuccess }) {
  const [qtyValue, setQtyValue] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleUpdateQty = async (actionType) => {
    const val = Number(qtyValue);
    if (isNaN(val) || val <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    if (actionType === "decrease" && val > item.qty) {
      toast.error(`Decrease quantity cannot exceed current qty (${item.qty})`);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/GarmentsProduct/updateItemQtyModal", {
        order_id: orderId,
        item_id: item.item_id,
        variant_id: item.variant_id,
        qty_change: val,
        action_type: actionType,
      });

      if (res.data?.success) {
        toast.success(res.data.message || "Quantity updated successfully!");
        onSuccess();
        onClose();
      } else {
        toast.error(res.data?.message || "Update failed");
      }
    } catch (error) {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 p-3 backdrop-blur-sm sm:p-4">
      <div className="w-full max-w-sm rounded-3xl bg-white p-4 shadow-2xl space-y-3 sm:max-w-md sm:p-6 animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 sm:h-10 sm:w-10">
              <PackagePlus size={18} />
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-900 sm:text-sm">Qty Adjust</h3>
              <p className="text-[10px] text-slate-500 line-clamp-1 sm:text-[11px]">{item.product_name} ({item.size} / {item.color})</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-1.5 text-xs bg-slate-50 p-3 rounded-2xl border border-slate-100">
          <div className="flex justify-between">
            <span className="text-slate-500">Current Qty:</span>
            <span className="font-bold text-slate-800">{item.qty} pcs</span>
          </div>
        </div>

        <div>
          <label className="text-[11px] font-bold text-slate-700 sm:text-xs">Enter Qty</label>
          <input
            type="number"
            min="1"
            value={qtyValue}
            onChange={(e) => setQtyValue(e.target.value)}
            className="w-full mt-1 rounded-xl border border-slate-200 bg-white p-2.5 text-xs font-bold text-slate-900 outline-none focus:border-indigo-500 sm:p-3 sm:text-sm"
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => handleUpdateQty("decrease")}
            disabled={loading}
            className="flex-1 rounded-xl bg-red-600 py-3 text-xs font-bold text-white shadow-md active:scale-95 transition"
          >
            {loading ? "..." : "Decrease (-)"}
          </button>
          <button
            onClick={() => handleUpdateQty("increase")}
            disabled={loading}
            className="flex-1 rounded-xl bg-emerald-600 py-3 text-xs font-bold text-white shadow-md active:scale-95 transition"
          >
            {loading ? "..." : "Increase (+)"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// 2. RETURN PRODUCT MODAL
// =====================================================
function ReturnProductModal({ item, orderId, onClose, onSuccess }) {
  const [returnQty, setReturnQty] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleReturn = async () => {
    const qtyNum = Number(returnQty);
    if (isNaN(qtyNum) || qtyNum <= 0) {
      toast.error("Please enter a valid return quantity");
      return;
    }
    if (qtyNum > item.qty) {
      toast.error(`Return quantity cannot exceed purchased quantity (${item.qty})`);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/GarmentsProduct/returnItem", {
        order_id: orderId,
        item_id: item.item_id,
        variant_id: item.variant_id,
        product_name: item.product_name,
        size: item.size,
        color: item.color,
        return_qty: qtyNum,
      });

      if (res.data?.success) {
        toast.success(res.data.message || "Product returned successfully!");
        onSuccess();
        onClose();
      } else {
        toast.error(res.data?.message || "Return failed");
      }
    } catch (error) {
      toast.error("Error processing return");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 p-3 backdrop-blur-sm sm:p-4">
      <div className="w-full max-w-sm rounded-3xl bg-white p-4 shadow-2xl space-y-3 sm:max-w-md sm:p-6 animate-in fade-in zoom-in duration-200">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 sm:h-10 sm:w-10">
              <RotateCcw size={18} />
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-900 sm:text-sm">Return Product</h3>
              <p className="text-[10px] text-slate-500 line-clamp-1 sm:text-[11px]">{item.product_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-1.5 text-xs bg-slate-50 p-3 rounded-2xl border border-slate-100">
          <div className="flex justify-between">
            <span className="text-slate-500">Variant:</span>
            <span className="font-bold text-slate-800">{item.size} / {item.color}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Purchased Qty:</span>
            <span className="font-bold text-slate-800">{item.qty} pcs</span>
          </div>
        </div>

        <div>
          <label className="text-[11px] font-bold text-slate-700 sm:text-xs">Enter Qty to Return (Max: {item.qty})</label>
          <input
            type="number"
            min="1"
            max={item.qty}
            value={returnQty}
            onChange={(e) => setReturnQty(e.target.value)}
            className="w-full mt-1 rounded-xl border border-slate-200 bg-white p-2.5 text-xs font-bold text-slate-900 outline-none focus:border-amber-500 sm:p-3 sm:text-sm"
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-3 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={handleReturn}
            disabled={loading}
            className="flex-1 rounded-xl bg-amber-600 py-3 text-xs font-bold text-white shadow-md active:scale-95 transition"
          >
            {loading ? "Processing..." : "Confirm Return"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// 3. EDIT ORDER MODAL (Separate Desktop & Mobile Views)
// =====================================================
function EditOrderModal({ orderId, onClose, onSaveSuccess }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orderForm, setOrderForm] = useState(null);
  const [returnHistory, setReturnHistory] = useState([]);
  const [selectedReturnItem, setSelectedReturnItem] = useState(null);
  const [selectedAddQtyItem, setSelectedAddQtyItem] = useState(null);

  const [allProducts, setAllProducts] = useState([]);
  const [searchProduct, setSearchProduct] = useState("");
  const [showAddProductBox, setShowAddProductBox] = useState(false);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
      fetchCatalogProducts();
      fetchReturnHistory();
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const res = await api.get(`/GarmentsProduct/getOrder/${orderId}`);
      if (res.data?.success) {
        setOrderForm(res.data.data);
      }
    } catch (error) {
      toast.error("Error loading order details");
    } finally {
      setLoading(false);
    }
  };

  const fetchReturnHistory = async () => {
    try {
      const res = await api.get(`/GarmentsProduct/getReturnHistory/${orderId}`);
      if (res.data?.success) {
        setReturnHistory(res.data.data || []);
      }
    } catch (e) {}
  };

  const fetchCatalogProducts = async () => {
    try {
      const res = await api.get("/GarmentsProduct/getProducts");
      setAllProducts(res.data?.data || []);
    } catch (e) {}
  };

  const handleItemRateChange = (index, newRateVal) => {
    const rate = Number(newRateVal);
    if (isNaN(rate) || rate < 0) return;

    const updatedItems = [...orderForm.items];
    updatedItems[index].rate = rate;
    updatedItems[index].total = Number(updatedItems[index].qty || 0) * rate;

    recalculateTotals(updatedItems, orderForm.discount, orderForm.gst);
  };

  const recalculateTotals = (items, discountVal, gstVal) => {
    const total = items.reduce((sum, item) => sum + Number(item.total || 0), 0);
    const discount = Number(discountVal || 0);
    const gst = Number(gstVal || 0);
    const net_total = total - discount + gst;

    setOrderForm((prev) => ({
      ...prev,
      items,
      total,
      net_total,
    }));
  };

  const handleAddNewItemToBill = async (product, variant) => {
    try {
      const res = await api.post("/GarmentsProduct/addItemToOrder", {
        order_id: orderId,
        garment_product_id: product.garment_product_id,
        variant_id: variant.variant_id,
        product_name: product.product_name,
        size: variant.size,
        color: variant.color,
        sku: variant.sku,
        qty: 1,
        rate: product.price,
      });

      if (res.data?.success) {
        toast.success(res.data.message || "Item added to bill!");
        setShowAddProductBox(false);
        setSearchProduct("");
        await fetchOrderDetails();
      }
    } catch (error) {
      toast.error("Failed to add product");
    }
  };

  const handleRemoveItem = async (item) => {
    if (!window.confirm(`Are you sure you want to remove ${item.product_name} from bill?`)) return;

    try {
      const res = await api.post("/GarmentsProduct/removeItem", {
        order_id: orderId,
        item_id: item.item_id,
        variant_id: item.variant_id,
        qty: item.qty,
      });

      if (res.data?.success) {
        toast.success("Item removed from bill");
        await fetchOrderDetails();
      }
    } catch (error) {
      toast.error("Error removing item");
    }
  };

  const handleDiscountChange = (val) => {
    const discount = Number(val || 0);
    recalculateTotals(orderForm.items, discount, orderForm.gst);
    setOrderForm((prev) => ({ ...prev, discount }));
  };

  const handleGstChange = (val) => {
    const gst = Number(val || 0);
    recalculateTotals(orderForm.items, orderForm.discount, gst);
    setOrderForm((prev) => ({ ...prev, gst }));
  };

  const handlePrintBill = () => {
    const printWindow = window.open("", "_blank", "width=300,height=600");
    if (!printWindow) {
      toast.error("Please allow popups for printing");
      return;
    }

    const htmlContent = `
      <html>
        <head>
          <title>Receipt #${orderForm?.invoice_no}</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: 11px;
              color: #000;
              width: 72mm;
              margin: 0 auto;
              padding: 5px;
              background: #fff;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .header { margin-bottom: 8mm; border-bottom: 1px dashed #000; padding-bottom: 5px; }
            .details { margin-bottom: 8mm; border-bottom: 1px dashed #000; padding-bottom: 5px; font-size: 10px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 8mm; }
            th, td { padding: 4px 0; text-align: left; font-size: 10px; }
            th { border-bottom: 1px dashed #000; }
            .totals { border-top: 1px dashed #000; padding-top: 5px; font-size: 10px; }
            .totals div { display: flex; justify-content: space-between; margin-bottom: 3px; }
            .net-total { font-size: 12px; font-weight: bold; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 4px 0; margin-top: 4px; }
            .footer { text-align: center; margin-top: 10px; font-size: 10px; border-top: 1px dashed #000; padding-top: 5px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h3 class="text-center" style="margin: 0;">GARMENTS POS</h3>
            <div class="text-center">Sales Receipt</div>
          </div>
          
          <div class="details">
            <div><strong>Inv:</strong> #${orderForm?.invoice_no}</div>
            <div><strong>Date:</strong> ${new Date(orderForm?.order_date || Date.now()).toLocaleDateString("en-IN")}</div>
            <div><strong>Cust:</strong> ${orderForm?.customer_name || "Walk-in"}</div>
            <div><strong>Phone:</strong> ${orderForm?.phone || "N/A"}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th class="text-center">Qty</th>
                <th class="text-right">Price</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${orderForm?.items?.map(i => `
                <tr>
                  <td>${i.product_name}<br/><span style="font-size:9px;">(${i.size}/${i.color})</span></td>
                  <td class="text-center">${i.qty}</td>
                  <td class="text-right">₹${Number(i.rate).toFixed(2)}</td>
                  <td class="text-right">₹${Number(i.total).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="totals">
            <div><span>Subtotal:</span> <span>₹${Number(orderForm?.total || 0).toFixed(2)}</span></div>
            <div><span>Discount:</span> <span>₹${Number(orderForm?.discount || 0).toFixed(2)}</span></div>
            <div><span>GST:</span> <span>₹${Number(orderForm?.gst || 0).toFixed(2)}</span></div>
            <div class="net-total"><span>NET TOTAL:</span> <span>₹${Number(orderForm?.net_total || 0).toFixed(2)}</span></div>
          </div>

          <div class="footer">
            Thank you for shopping!<br/>Please visit again.
          </div>

          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => { window.close(); }, 500);
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSaveAllChanges = async () => {
    if (!orderForm.items || orderForm.items.length === 0) {
      toast.error("Bill cannot be empty!");
      return;
    }

    setSaving(true);
    try {
      const res = await api.put(`/GarmentsProduct/updateOrder/${orderId}`, orderForm);
      if (res.data?.success) {
        toast.success("Order & inventory stock synchronized successfully!");
        onSaveSuccess();
        onClose();
      } else {
        toast.error(res.data?.message || "Failed to update order");
      }
    } catch (error) {
      toast.error("Server error during update");
    } finally {
      setSaving(false);
    }
  };

  const filteredCatalog = allProducts.filter((p) =>
    p.product_name.toLowerCase().includes(searchProduct.toLowerCase()) ||
    p.product_code?.toLowerCase().includes(searchProduct.toLowerCase())
  );

  if (!orderId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 p-0 sm:p-4 backdrop-blur-md">
      <div className="relative flex h-full w-full flex-col bg-white shadow-2xl sm:max-h-[92vh] sm:max-w-4xl sm:rounded-3xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-3 sm:px-6 sm:py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-md sm:h-10 sm:w-10">
              <Receipt size={18} />
            </div>
            <div>
              <h2 className="text-xs font-bold text-slate-900 sm:text-base">
                Edit Order #{orderForm?.invoice_no}
              </h2>
              <p className="text-[10px] text-slate-500 sm:text-[11px]">Manage Qty, Rate & Thermal Print</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={handlePrintBill}
              className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-3 py-2 text-[11px] font-bold text-white shadow-sm hover:bg-emerald-700 sm:text-xs"
            >
              <Printer size={13} />
              <span className="hidden xs:inline">Print</span>
            </button>
            <button onClick={onClose} className="rounded-xl bg-slate-100 p-2 text-slate-500 hover:bg-slate-200">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <LoaderCircle size={32} className="animate-spin text-indigo-600" />
          </div>
        ) : orderForm ? (
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
            {/* Customer Details */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-3 sm:p-4 space-y-2">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-[11px]">Customer Info</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500">Name</label>
                  <input
                    type="text"
                    value={orderForm.customer_name || ""}
                    onChange={(e) => setOrderForm({ ...orderForm, customer_name: e.target.value })}
                    className="w-full mt-0.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500">Phone</label>
                  <input
                    type="text"
                    value={orderForm.phone || ""}
                    onChange={(e) => setOrderForm({ ...orderForm, phone: e.target.value })}
                    className="w-full mt-0.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800"
                  />
                </div>
              </div>
            </div>

            {/* Bill Line Items */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-2 sm:mb-3">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-[11px]">Bill Items</h3>
                <button
                  onClick={() => setShowAddProductBox(!showAddProductBox)}
                  className="inline-flex items-center gap-1 rounded-xl bg-indigo-600 px-3 py-1.5 text-[11px] font-bold text-white shadow-sm hover:bg-indigo-700 sm:text-xs sm:px-3.5 sm:py-2"
                >
                  <Plus size={13} />
                  <span>Add Product</span>
                </button>
              </div>

              {/* Add Product Dropdown Box */}
              {showAddProductBox && (
                <div className="mb-3 rounded-2xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
                  <h4 className="text-xs font-bold text-indigo-900">Search Product to Add:</h4>
                  <input
                    type="text"
                    placeholder="Search garment name..."
                    value={searchProduct}
                    onChange={(e) => setSearchProduct(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2.5 text-xs font-semibold outline-none"
                  />

                  {searchProduct && (
                    <div className="max-h-40 overflow-y-auto rounded-xl bg-white border border-slate-200 divide-y">
                      {filteredCatalog.map((p) => (
                        <div key={p.garment_product_id} className="p-2.5">
                          <p className="text-xs font-bold text-slate-800">{p.product_name} - ₹{p.price}</p>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {p.variants?.map((v) => (
                              <button
                                key={v.variant_id}
                                onClick={() => handleAddNewItemToBill(p, v)}
                                className="rounded-lg bg-indigo-50 border border-indigo-100 px-2.5 py-1 text-[10px] font-bold text-indigo-700 hover:bg-indigo-600 hover:text-white transition"
                              >
                                {v.size} / {v.color} (Stock: {v.qty})
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ================================================= */}
              {/* DESKTOP VIEW CODE (Table Format) - Visible only on md and above */}
              {/* ================================================= */}
              <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                    <tr>
                      <th className="p-3 font-semibold">Item</th>
                      <th className="p-3 font-semibold">Variant</th>
                      <th className="p-3 font-semibold text-center">Qty</th>
                      <th className="p-3 font-semibold">Rate (₹)</th>
                      <th className="p-3 font-semibold text-right">Total</th>
                      <th className="p-3 font-semibold text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orderForm.items?.map((item, idx) => (
                      <tr key={item.item_id || idx} className="hover:bg-slate-50/50">
                        <td className="p-3 font-bold text-slate-900">{item.product_name}</td>
                        <td className="p-3 text-slate-500 font-medium">{item.size} / {item.color}</td>
                        <td className="p-3 text-center">
                          <div className="inline-flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-xl">
                            <span className="font-bold text-slate-800">{item.qty} pcs</span>
                            <button
                              type="button"
                              onClick={() => setSelectedAddQtyItem(item)}
                              className="flex h-5 px-1.5 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-bold text-[10px]"
                              title="Adjust Qty"
                            >
                              ± Qty
                            </button>
                          </div>
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            min="0"
                            value={item.rate}
                            onChange={(e) => handleItemRateChange(idx, e.target.value)}
                            className="w-20 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 font-semibold text-slate-800 outline-none focus:border-indigo-500"
                          />
                        </td>
                        <td className="p-3 text-right font-extrabold text-slate-900">
                          ₹{Number(item.total || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setSelectedReturnItem(item)}
                              className="rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700 hover:bg-amber-100"
                            >
                              Return
                            </button>
                            <button
                              onClick={() => handleRemoveItem(item)}
                              className="rounded-lg bg-red-50 px-2 py-1 text-[10px] font-bold text-red-600 hover:bg-red-100"
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ================================================= */}
              {/* MOBILE VIEW CODE (Card Format) - Visible only below md */}
              {/* ================================================= */}
              <div className="md:hidden space-y-3">
                {orderForm.items?.map((item, idx) => (
                  <div key={item.item_id || idx} className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="text-xs font-bold text-slate-900">{item.product_name}</h4>
                        <p className="text-[11px] text-slate-500 font-medium">Variant: {item.size} / {item.color}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-extrabold text-slate-900">₹{Number(item.total || 0).toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2.5 py-1.5 rounded-xl">
                          {item.qty} pcs
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedAddQtyItem(item)}
                          className="inline-flex items-center gap-1 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-indigo-700 transition"
                        >
                          <PackagePlus size={13} />
                          <span>Qty ±</span>
                        </button>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-semibold text-slate-400">Rate:</span>
                        <input
                          type="number"
                          min="0"
                          value={item.rate}
                          onChange={(e) => handleItemRateChange(idx, e.target.value)}
                          className="w-20 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-50">
                      <button
                        onClick={() => setSelectedReturnItem(item)}
                        className="rounded-lg bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700 hover:bg-amber-100"
                      >
                        Return
                      </button>
                      <button
                        onClick={() => handleRemoveItem(item)}
                        className="rounded-lg bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-600 hover:bg-red-100"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Return History Table */}
            {returnHistory.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <History size={15} className="text-amber-600" />
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-amber-800 sm:text-[11px]">Return History Log</h3>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-amber-200 bg-amber-50/30 shadow-sm">
                  <table className="w-full text-left text-xs min-w-[450px]">
                    <thead className="bg-amber-100/60 text-amber-900 border-b border-amber-200">
                      <tr>
                        <th className="p-2.5 font-semibold">Product</th>
                        <th className="p-2.5 font-semibold">Variant</th>
                        <th className="p-2.5 font-semibold text-center">Returned Qty</th>
                        <th className="p-2.5 font-semibold text-right">Date & Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {returnHistory.map((ret, rIdx) => (
                        <tr key={ret.return_id || rIdx} className="hover:bg-amber-50/50">
                          <td className="p-2.5 font-bold text-slate-900">{ret.product_name}</td>
                          <td className="p-2.5 text-slate-600">{ret.size} / {ret.color}</td>
                          <td className="p-2.5 text-center font-extrabold text-amber-700">-{ret.return_qty} pcs</td>
                          <td className="p-2.5 text-right font-medium text-slate-500">
                            {new Date(ret.return_date).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Calculations Box */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3.5 sm:p-4">
              <div>
                <label className="text-[10px] font-semibold text-slate-500">Payment Mode</label>
                <select
                  value={orderForm.payment_mode || "Cash"}
                  onChange={(e) => setOrderForm({ ...orderForm, payment_mode: e.target.value })}
                  className="w-full mt-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 outline-none"
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                </select>
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal:</span>
                  <span className="font-bold text-slate-800">₹{Number(orderForm.total || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500 font-semibold">Discount (₹):</span>
                  <input
                    type="number"
                    min="0"
                    value={orderForm.discount || 0}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    className="w-24 rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-right font-bold text-slate-900"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500 font-semibold">GST (₹):</span>
                  <input
                    type="number"
                    min="0"
                    value={orderForm.gst || 0}
                    onChange={(e) => handleGstChange(e.target.value)}
                    className="w-24 rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-right font-bold text-slate-900"
                  />
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2 text-sm font-extrabold text-slate-900">
                  <span>Net Total:</span>
                  <span className="text-indigo-600">₹{Number(orderForm.net_total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-3 py-3 sm:px-6">
          <button onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-600">
            Cancel
          </button>
          <button
            onClick={handleSaveAllChanges}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-5 py-2.5 text-xs font-bold text-white shadow-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? <LoaderCircle size={14} className="animate-spin" /> : <Save size={14} />}
            <span>Save All Changes</span>
          </button>
        </div>
      </div>

      {selectedReturnItem && (
        <ReturnProductModal
          item={selectedReturnItem}
          orderId={orderId}
          onClose={() => setSelectedReturnItem(null)}
          onSuccess={() => {
            fetchOrderDetails();
            fetchReturnHistory();
          }}
        />
      )}

      {selectedAddQtyItem && (
        <AddQtyModal
          item={selectedAddQtyItem}
          orderId={orderId}
          onClose={() => setSelectedAddQtyItem(null)}
          onSuccess={fetchOrderDetails}
        />
      )}
    </div>
  );
}

// =====================================================
// 4. MAIN ORDERS LIST PAGE
// =====================================================
export default function GarmentsOrderList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editOrderId, setEditOrderId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get("/GarmentsProduct/getOrders");
      if (res.data?.success) {
        setOrders(res.data.data || []);
      }
    } catch (error) {
      toast.error("Error loading order list");
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const q = searchQuery.toLowerCase();
    return (
      order.invoice_no?.toLowerCase().includes(q) ||
      order.customer_name?.toLowerCase().includes(q) ||
      order.phone?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-3 sm:px-6 sm:py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-md sm:h-10 sm:w-10">
              <Receipt size={18} />
            </div>
            <div>
              <h1 className="text-sm font-extrabold text-slate-900 sm:text-xl">
                Garments Sales Orders
              </h1>
              <p className="text-[10px] text-slate-500 sm:text-xs">Manage bills, rates & thermal printing</p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-3 pt-4 sm:px-6 sm:pt-6">
        <div className="mb-4 max-w-md sm:mb-5">
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search invoice, customer, or phone..."
              className="w-full rounded-2xl border border-slate-200 bg-white pl-10 pr-4 py-2.5 text-xs sm:text-sm text-slate-800 outline-none shadow-sm transition focus:border-indigo-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3">
            <LoaderCircle size={28} className="animate-spin text-indigo-600" />
            <p className="text-xs font-medium text-slate-500">Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white p-6 text-center">
            <Receipt size={36} className="text-slate-300 mb-2" />
            <h3 className="text-sm font-bold text-slate-800">No orders found</h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredOrders.map((order) => (
              <div key={order.order_id} className="flex flex-col justify-between rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-indigo-300">
                <div>
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                    <span className="font-mono text-xs font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg">
                      #{order.invoice_no}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-xl bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      <CheckCircle2 size={11} /> {order.status || "Completed"}
                    </span>
                  </div>

                  <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-slate-400 shrink-0" />
                      <span className="font-bold text-slate-900 truncate">{order.customer_name || "Walk-in"}</span>
                    </div>
                    {order.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-slate-400 shrink-0" />
                        <span>{order.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-slate-400 shrink-0" />
                      <span>{new Date(order.order_date).toLocaleDateString("en-IN")}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-slate-400">Net Payable</span>
                    <span className="text-base font-extrabold text-slate-900">
                      ₹{Number(order.net_total || 0).toLocaleString("en-IN")}
                    </span>
                  </div>

                  <button
                    onClick={() => setEditOrderId(order.order_id)}
                    className="mt-2.5 w-full flex items-center justify-center gap-1.5 rounded-xl bg-indigo-50 py-3 text-xs font-bold text-indigo-600 hover:bg-indigo-100 active:scale-95 transition"
                  >
                    <Edit size={13} />
                    <span>Edit Order & Print</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editOrderId && (
        <EditOrderModal
          orderId={editOrderId}
          onClose={() => setEditOrderId(null)}
          onSaveSuccess={fetchOrders}
        />
      )}
    </div>
  );
}